import type { ScheduleResult } from "@planerius/contracts/cabinet"

import { minutesFromDayStart } from "./journal-time"

/**
 * Свободные окна мастера — считаются НА КЛИЕНТЕ из уже загруженного дня.
 *
 * ПОЧЕМУ НЕ ЗАПРОСОМ. Кабинетного маршрута свободного времени в API нет: есть только
 * публичный GET /v1/public/:tenantSlug/slots, и он отдаёт 404 service-not-bookable,
 * если хоть одна услуга корзины не выставлена в онлайн-запись
 * (apps/api/src/modules/slots/slots.service.ts). Для журнала это неверно по смыслу:
 * администратор записывает и на те услуги, которых нет на витрине, — иначе половина
 * прайса салона оказывается недоступна с его же стойки. Пробел вынесен в отчёт.
 *
 * ЧТО ЭТО МЕНЯЕТ ДЛЯ ЧЕЛОВЕКА. Предложенное окно — ПОДСКАЗКА, а не бронь: пока он
 * выбирал, время могли занять снова. Поэтому «слот заняли» обрабатывается как штатный
 * исход и на второй попытке тоже, а не считается невозможным.
 *
 * Файл чистый: ни React, ни запросов. Свободное время — это арифметика на отрезках,
 * и проверять её надо расчётом.
 */

export interface BusyInterval {
  readonly startMinute: number
  readonly endMinute: number
}

export interface FreeSlotQuery {
  readonly result: ScheduleResult
  /** Календарная дата филиала, к началу которой считаются минуты. */
  readonly date: string
  readonly employeeId: string
  readonly durationMinutes: number
  /** Шаг, по которому предлагаются начала. Шаг филиала, а не произвольная минута. */
  readonly stepMinutes: number
  /** Раньше этой минуты не предлагать: прошедшее время — не свободное. */
  readonly notBeforeMinute?: number
  readonly limit?: number
}

/**
 * Занятость мастера в этот день одним списком.
 *
 * ТОНКОЕ МЕСТО — ПАУЗА СОСТАВНОЙ УСЛУГИ. В окрашивании есть выдержка: кресло занято,
 * а мастер свободен, и в это время в него САЖАЮТ другого клиента (ТЗ §2.4.2). Поэтому
 * запись со списком шагов занимает мастера только теми шагами, где occupiesEmployee,
 * а не целиком: иначе сорок минут выдержки исчезают из свободного времени салона
 * каждый раз, когда кто-то красится.
 */
export function busyIntervals(query: {
  readonly result: ScheduleResult
  readonly date: string
  readonly employeeId: string
}): readonly BusyInterval[] {
  const { result, date, employeeId } = query
  const zone = result.branch.timezone
  const at = (instant: string): number => minutesFromDayStart(instant, zone, date)
  const out: BusyInterval[] = []

  for (const appointment of result.appointments) {
    if (appointment.employeeId !== employeeId) continue
    // Отменённая запись время не занимает — иначе отменённый визит навсегда съедает слот.
    if (appointment.status === "cancelled") continue

    const occupying = appointment.steps.filter((step) => step.occupiesEmployee)
    if (occupying.length === 0) {
      out.push({ startMinute: at(appointment.startAt), endMinute: at(appointment.endAt) })
      continue
    }
    for (const step of occupying) {
      out.push({ startMinute: at(step.startAt), endMinute: at(step.endAt) })
    }
  }

  // Буфер и уборка входят сюда наравне с перерывом: они системные, но время занимают
  // по-настоящему — записать «впритык» в буфер значит выпустить мастера без уборки кресла.
  for (const block of result.busy) {
    if (block.employeeId !== employeeId) continue
    out.push({ startMinute: at(block.startAt), endMinute: at(block.endAt) })
  }

  // ЧУЖОЕ удержание — занято: за ним прямо сейчас живой человек вводит код из SMS.
  // СВОЁ удержание не мешает: оно и поставлено ради этой самой записи.
  //
  // КАРАНТИН СВОБОДЕН. Он придерживает освободившееся время от ОНЛАЙН-записи, чтобы
  // администратор успел распорядиться им сам (2-2.4.1, решение 43), — а здесь считается
  // время именно для его, администратора, записи. Показывать карантин занятым значило бы
  // прятать от стойки ровно тот слот, ради которого карантин и придуман; сервер такую
  // запись принимает (cabinet-appointments.service снимает карантин в той же транзакции).
  for (const hold of result.holds) {
    if (hold.employeeId !== employeeId || hold.isOwn || hold.kind === "quarantine") continue
    out.push({ startMinute: at(hold.startAt), endMinute: at(hold.endAt) })
  }

  return out.toSorted((a, b) => a.startMinute - b.startMinute)
}

/** Рабочее окно мастера в этот день. null — сегодня он не работает: это не «занят». */
export function workWindow(query: {
  readonly result: ScheduleResult
  readonly date: string
  readonly employeeId: string
}): BusyInterval | null {
  const workday = query.result.workdays.find(
    (item) => item.employeeId === query.employeeId && item.date === query.date,
  )
  if (workday === undefined) return null
  const zone = query.result.branch.timezone
  return {
    startMinute: minutesFromDayStart(workday.startAt, zone, query.date),
    endMinute: minutesFromDayStart(workday.endAt, zone, query.date),
  }
}

function overlaps(from: number, to: number, interval: BusyInterval): boolean {
  // Касание границами конфликтом НЕ является: запись 14:00–14:45 и запись 14:45–15:00
  // стоят подряд, и это обычная смена.
  return from < interval.endMinute && to > interval.startMinute
}

/**
 * Свободные начала для услуги нужной длины.
 *
 * Начала предлагаются по шагу филиала, а не по минуте: «ближайшее 14:47» человеку
 * не нужно — он проговаривает время вслух в трубку.
 */
export function freeWindows(query: FreeSlotQuery): readonly number[] {
  const work = workWindow(query)
  if (work === null) return []

  const duration = Math.max(query.durationMinutes, 1)
  const step = Math.max(query.stepMinutes, 5)
  const busy = busyIntervals(query)
  const limit = query.limit ?? 3
  const notBefore = query.notBeforeMinute ?? Number.NEGATIVE_INFINITY

  const first = Math.max(work.startMinute, notBefore)
  // Округление вверх по шагу считается ОТ НАЧАЛА СМЕНЫ, а не от полуночи: смена
  // с 10:30 при шаге 15 даёт 10:30, 10:45 — а не 10:45, 11:00 с потерянной четвертью часа.
  const offset = Math.max(first - work.startMinute, 0)
  const start = work.startMinute + Math.ceil(offset / step) * step

  const found: number[] = []
  for (let minute = start; minute + duration <= work.endMinute; minute += step) {
    if (busy.some((interval) => overlaps(minute, minute + duration, interval))) continue
    found.push(minute)
    if (found.length >= limit) break
  }
  return found
}
